import React, { useMemo } from 'react';
import { BarChart, Bar, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
import MetricCard from './MetricCard';
import StatDisplay from './StatDisplay';
import { getPercentChange, axisStyle } from './marketUtils';
import {ArrowDown, ChartNoAxesColumn, ArrowUpWideNarrow, ArrowDownWideNarrow} from "lucide-react";

const VolatilityCard = ({ data: { vix, vix9d, vix6m } }) => {
  const chartData = useMemo(() => {
    if (!vix || !vix9d || !vix6m) return [];
    return vix.map((point, i) => ({
      timestamp: point.timestamp,
      // Stack the VIX measures from shortest to longest duration
      'VIX-9D': vix9d[i]?.close || 0,
      'VIX-30D': point.close - (vix9d[i]?.close || 0), // Difference for stacking
      'VIX-6M': vix6m[i]?.close - point.close, // Difference for stacking
      // Keep original values for stats
      'VIX': point.close,
      'VIX-6M-actual': vix6m[i]?.close || 0,
      vixInverted: vix9d[i]?.close > point.close,
    }));
  }, [vix, vix9d, vix6m]);

  const latestData = chartData[chartData.length - 1] || {};
  const weekPriorData = chartData[chartData.length - 6] || {};

  const vix9dChange = getPercentChange(latestData['VIX-9D'], weekPriorData['VIX-9D']);
  const vixChange = getPercentChange(latestData['VIX'], weekPriorData['VIX']);
  const vix6mChange = getPercentChange(latestData['VIX-6M-actual'], weekPriorData['VIX-6M-actual']);

  // Calculate spreads
  const longShortSpread = (latestData['VIX-6M-actual'] - latestData['VIX-9D']).toFixed(2);
  const longVixSpread = (latestData['VIX-6M-actual'] - latestData['VIX']).toFixed(2);
  const shortVixSpread = (latestData['VIX-9D'] - latestData['VIX']).toFixed(2);

  const marketVolType = useMemo(() => {
    if (parseFloat(longShortSpread) > 0 && parseFloat(longVixSpread) > 0) {
      return 'contango';
    }
    else if (parseFloat(longShortSpread) < 0 || parseFloat(shortVixSpread) > 0) {
      return 'backwardation';
    }
    return 'contango';
  }, [longShortSpread, longVixSpread, shortVixSpread]);

  const isCurrentlyInverted = marketVolType === 'backwardation';

  const headerRight = (
    <div className={`flex items-center gap-2 py-0.5 px-2 rounded-full border-0.5 ${isCurrentlyInverted ? 'text-indigo-700 border border-indigo-600' : 'text-sky-700 border border-sky-600'} font-medium`}>
      {isCurrentlyInverted ? 'Backwardation' : 'Contango'}
      {isCurrentlyInverted ?
        <ArrowDownWideNarrow className="w-5 h-5" /> :
        <ArrowUpWideNarrow className="w-5 h-5" />
      }
    </div>
  );

  const statsContent = (
    <div className="grid grid-cols-3 gap-4">
      <div className="p-2">
        <StatDisplay
          title="VIX 9 Day"
          value={latestData['VIX-9D']?.toFixed(1)}
          change={parseFloat(vix9dChange)}
          period="1 week"
          color={'#1e293b'}
        />
      </div>
      <div className="p-2">
        <StatDisplay
          title="VIX 30 Day"
          value={latestData['VIX']?.toFixed(1)}
          change={parseFloat(vixChange)}
          period="1 week"
        />
      </div>
      <div className="p-2">
        <StatDisplay
          title="VIX 6 Month"
          value={latestData['VIX-6M-actual']?.toFixed(1)}
          change={parseFloat(vix6mChange)}
          period="1 week"
        />
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const vix9d = payload[0].value;
      const vix30d = vix9d + payload[1].value;
      const vix6m = vix30d + payload[2].value;

      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow">
          <p className="text-sm">VIX-9D: {vix9d.toFixed(2)}</p>
          <p className="text-sm">VIX-30D: {vix30d.toFixed(2)}</p>
          <p className="text-sm">VIX-6M: {vix6m.toFixed(2)}</p>
        </div>
      );
    }
    return null;
  };

  const chartContent = (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} stackOffset="none">
        <YAxis
          {...axisStyle}
          type="number"
          domain={[16, 40]}
          ticks={[16, 24, 32, 40]}
        />
        <XAxis
          {...axisStyle}
          dataKey="timestamp"
          height={20}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar
          dataKey="VIX-9D"
          stackId="a"
          fill="#1e293b"
          isAnimationActive={false}
        />
        <Bar
          dataKey="VIX-30D"
          stackId="a"
          fill="#ec4899"
          isAnimationActive={false}
        />
        <Bar
          dataKey="VIX-6M"
          stackId="a"
          fill="#3b82f6"
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const bottomContent = (
    <div className="grid grid-cols-3 gap-4 px-2">
      <div>
        <div className="text-lg card-value text-gray-900">
          {longShortSpread}
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">VIX-6M / VIX-9D</div>
      </div>
      <div>
        <div className="text-lg card-value text-gray-900">
          {longVixSpread}
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">VIX-6M / VIX</div>
      </div>
      <div>
        <div className={`text-lg card-value ${isCurrentlyInverted ? 'text-red-500' : 'text-green-500'}`}>
          {chartData.filter(d => d.vixInverted).length} days
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">Inverted</div>
      </div>
    </div>
  );

  return (
    <MetricCard
      title="Volatility"
      headerRight={headerRight}
      statsContent={statsContent}
      chartContent={chartContent}
      bottomContent={bottomContent}
      titleIcon={<ChartNoAxesColumn size={24} color={'#4f46e5'} strokeWidth={2} />}
    />
  );
};

export default VolatilityCard;